import { memo, useState } from "react";
import { FilterOutlined } from "@ant-design/icons";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Button, Checkbox, Dropdown, Empty, List, Space, type DropdownProps } from "antd";
import { useTranslation } from "react-i18next";

import { TraineeEventType } from "@fitness-app/data-models/businessEvents/domain/trainee/TraineeEvents";
import { TraineeActivityEventType } from "@fitness-app/data-models/businessEvents/domain/traineeActivity/TraineeActivityEvents";
import { type ClientActivityEvent } from "@fitness-app/data-models/entities/ClientActivity";

import ActivityEventItem from "~/modules/Trainee/TraineeProfile/components/ActivityEventItem";
import { traineeKeys, useTraineeId } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase } from "~/store/initializeStore";

type FilterType = TraineeActivityEventType | TraineeEventType;

const ActivityFilters = memo(
  ({
    selectedKeys,
    setSelectedKeys,
  }: {
    selectedKeys: FilterType[];
    setSelectedKeys: (keys: FilterType[]) => void;
  }) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation(["dashboard", "common"]);
    const filters = [
      TraineeActivityEventType.TraineeAddedWeightMeasurement,
      TraineeActivityEventType.TraineeAddedBodyMeasurements,
      TraineeActivityEventType.TraineeAddedBodyPhotos,
      TraineeActivityEventType.TraineeTrackedWaterConsumption,
      TraineeActivityEventType.TraineeAddedWorkoutRating,
      TraineeActivityEventType.TraineeAddedDishRating,
      TraineeActivityEventType.TraineeSentSurveyAnswers,
      TraineeActivityEventType.TraineeSentExerciseRecording,
      TraineeEventType.TraineeAssignedToTrainer,
      TraineeEventType.TraineeRemovedFromTrainer,
      TraineeEventType.TraineeAddedToApp,
      TraineeEventType.TraineeActivatedAccount,
      TraineeEventType.TraineeAccountDeactivated,
      TraineeEventType.TraineeStartedWorkoutProgram,
      TraineeEventType.TraineeGotAccessToWorkoutProgram,
      TraineeEventType.TraineeEndedWorkoutProgram,
      TraineeEventType.TraineeStartedNutritionPlan,
      TraineeEventType.TraineeGotAccessToNutritionPlan,
      TraineeEventType.TraineeEndedNutritionPlan,
      TraineeEventType.TraineeGhostChanged,
      TraineeActivityEventType.TraineeAttendedCalendarEvent,
    ].map((event) => ({
      key: event,
      label: (
        <Checkbox onClick={(ev) => ev.stopPropagation()} checked={selectedKeys.includes(event)}>
          {t(`common:${event}`)}
        </Checkbox>
      ),
    }));

    const handleOpenChange: DropdownProps["onOpenChange"] = (nextOpen, info) => {
      if (info.source === "trigger" || nextOpen) {
        setOpen(nextOpen);
      }
    };

    return (
      <Space size={4}>
        {selectedKeys.length > 0 && (
          <Button onClick={() => setSelectedKeys([])} type="link">
            {t("common:button.reset")}
          </Button>
        )}
        <Dropdown
          onOpenChange={handleOpenChange}
          open={open}
          menu={{
            items: filters,
            selectable: true,
            selectedKeys,
            multiple: true,
            onDeselect: ({ selectedKeys }) => {
              setSelectedKeys(selectedKeys as FilterType[]);
            },
            onSelect: ({ selectedKeys }) => {
              setSelectedKeys(selectedKeys as FilterType[]);
            },
          }}
        >
          <Button type={selectedKeys.length ? "primary" : undefined} icon={<FilterOutlined />} />
        </Dropdown>
      </Space>
    );
  },
);

ActivityFilters.displayName = "ActivityFilters";

// `pageSize` ze stanu początkowego slice'a trainee w app-store.
const PAGE_SIZE = 30;

const EXCLUDED_EVENT_NAMES = [
  TraineeActivityEventType.TraineeSentChatMessage,
  TraineeEventType.TraineeGotMessageFromTrainer,
];

/** Zdarzenia klientki stronami (to samo zapytanie co `fetchClientActivityEvents`). */
const useClientActivityEvents = (selectedKeys: FilterType[]) => {
  const traineeId = useTraineeId();

  return useInfiniteQuery({
    queryKey: [...traineeKeys.activityEvents(traineeId), selectedKeys],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const start = (pageParam - 1) * PAGE_SIZE;
      const stop = pageParam * PAGE_SIZE - 1;

      let query = supabase.from("client_activity_event").select("*", { count: "exact" }).eq("traineeId", traineeId);

      if (selectedKeys.length) {
        query = query.in("eventName", selectedKeys);
      } else {
        query = query.not("eventName", "in", `(${EXCLUDED_EVENT_NAMES.join(",")})`);
      }

      const { data, error, count } = await query
        .order("timestamp", { ascending: false })
        .range(start, stop)
        .limit(PAGE_SIZE)
        .returns<ClientActivityEvent[]>();

      if (error) {
        throw new Error(error.message);
      }

      return { data, currentPage: pageParam, totalPages: count ? Math.ceil(count / PAGE_SIZE) : 0 };
    },
    getNextPageParam: (lastPage) => (lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined),
  });
};

export const LatestActivityContent: React.FC = () => {
  const { t } = useTranslation("trainees");
  const [selectedKeys, setSelectedKeys] = useState<FilterType[]>([]);
  const { data, isLoading, isSuccess, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useClientActivityEvents(selectedKeys);

  const clientActivityEvents = data?.pages.flatMap((page) => page.data) ?? [];

  const loadMore =
    isSuccess && hasNextPage ? (
      <div
        style={{
          textAlign: "center",
          marginTop: 12,
          height: 32,
          lineHeight: "32px",
        }}
      >
        <Button loading={isFetchingNextPage} onClick={() => void fetchNextPage()}>
          Pobierz więcej
        </Button>
      </div>
    ) : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <List loading />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <ActivityFilters selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys} />
      </div>
      <List
        itemLayout="vertical"
        locale={{
          emptyText: (
            <Empty
              imageStyle={{ marginTop: 80 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("emptyActivityList")}
            />
          ),
        }}
        loadMore={loadMore}
        dataSource={clientActivityEvents}
        renderItem={(item) => <ActivityEventItem event={item} />}
      />
    </div>
  );
};
